import { useEffect, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import EnquiryModal from "@/components/feature/EnquiryModal";
import { brand } from "@/mocks/siteData";
import type { Farm } from "@/mocks/farms";
import { usePublicFarms } from "@/hooks/usePublicCatalog";
import FarmsHero from "./components/FarmsHero";
import FarmsMap from "./components/FarmsMap";
import FarmsListings from "./components/FarmsListings";
import FarmsCta from "./components/FarmsCta";

export default function Farms() {
  const { data: farms, loading, error, reload } = usePublicFarms();
  const [selectedSlug, setSelectedSlug] = useState("");
  // Set while the "notify me when it reopens" form is open for a closed farm.
  const [notifyFarm, setNotifyFarm] = useState<Farm | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (!selectedSlug && farms.length > 0) setSelectedSlug(farms[0].slug);
  }, [farms, selectedSlug]);

  const selectedFarm = farms.find((farm) => farm.slug === selectedSlug) ?? farms[0];

  const handleSelect = (slug: string) => {
    setSelectedSlug(slug);
    mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Rendered in every state (loading / error / ready) so the page always keeps
  // its own title, description and canonical link instead of falling back to
  // the site defaults while data loads.
  const pageSeo = (
    <Seo
      title={`Discover Farms in Konaseema | ${brand.name}`}
      description="Explore real working farms across the Godavari delta — coconut groves, paddy fields and family farms you can visit. Browse by crop and village on a live map."
      path="/farms"
      keywords="farms in Konaseema, visit a farm in Andhra Pradesh, farm tourism India, agritourism farms, discover farms, Godavari delta farms"
      image="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20lush%20green%20paddy%20fields%20and%20coconut%20groves%20on%20a%20working%20farm%20in%20Konaseema%20India%2C%20sunny%20daylight%2C%20fresh%20green%20tones%2C%20warm%20golden%20light%2C%20cinematic%20landscape%2C%20ultra%20detailed&width=1920&height=1080&seq=farms-list-cover&orientation=landscape"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Discover Farms in Konaseema",
        description:
          "A directory of real working farms across the Godavari delta that welcome visitors.",
        inLanguage: "en",
      }}
    />
  );

  if (farms.length === 0 && loading) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {pageSeo}
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-foreground-600">
            <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
            <p className="text-sm">Loading farms…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (farms.length === 0) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {pageSeo}
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <div className="text-center max-w-lg">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold">
              We couldn't load the farms
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {error || "Please try again in a moment."}
            </p>
            <button
              type="button"
              onClick={reload}
              className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Try again
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      {pageSeo}
      <Navbar />
      <main>
        <FarmsHero farms={farms} />
        <div ref={mapRef}>{selectedFarm && <FarmsMap farm={selectedFarm} />}</div>
        <FarmsListings
          farms={farms}
          selectedSlug={selectedSlug}
          onSelect={handleSelect}
          onNotify={setNotifyFarm}
        />
        <FarmsCta />
      </main>
      <Footer />
      <EnquiryModal
        open={Boolean(notifyFarm)}
        onClose={() => setNotifyFarm(null)}
        kind="farm"
        intent="notify"
        options={farms.map((farm) => ({ value: farm.name, label: farm.name }))}
        defaultValue={notifyFarm?.name ?? ""}
      />
    </div>
  );
}